// Keep first: must run before react-speech-recognition is evaluated (no-op outside the native app).
import './speech/installNativeSpeechRecognition';
import React from 'react';
import ReactDOM from 'react-dom/client';
import process from 'process';
import './index.css';
import App from './App';
import { store, persistedStore } from './store';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { setupGlobalErrorHandlers } from './utils/globalErrorHandler';
import { NetworkGate } from './offline/NetworkGate';
import OfflineBanner from './offline/OfflineBanner';
import OfflineEventsBridge from './offline/OfflineEventsBridge';
import OfflineDraftAutoSync from './offline/OfflineDraftAutoSync';
import SyncProgressBar from './offline/SyncProgressBar';

// Native WebView: file:// + ReactNativeWebView → use HashRouter (see App.js)
if (typeof window !== 'undefined' && window.ReactNativeWebView && !window.isNativeApp) {
  window.isNativeApp = true;
}

// Ensure Node-style process global exists for dependencies (e.g. simple-peer/readable-stream)
if (typeof window !== 'undefined') {
  // Merge process object with window.process if it exists, otherwise use the imported one
  if (!window.process) {
    window.process = { ...process };
  } else {
    // Merge properties from imported process into window.process
    Object.assign(window.process, process);
  }
  
  // Ensure process.nextTick is available (required by readable-stream)
  // The process package should have nextTick, but ensure it's there
  if (!window.process.nextTick || typeof window.process.nextTick !== 'function') {
    window.process.nextTick = function(callback, ...args) {
      // Use setImmediate if available, otherwise fall back to setTimeout
      if (typeof setImmediate !== 'undefined') {
        setImmediate(() => callback(...args));
      } else {
        setTimeout(() => callback(...args), 0);
      }
    };
  }
  
  // Ensure process.browser is set (some packages check for this)
  if (window.process.browser === undefined) {
    window.process.browser = true;
  }
  
  // Ensure process.env exists (some packages check for this)
  if (!window.process.env) {
    window.process.env = {};
  }
}

// Polyfill for toSorted method (ES2023) for older browsers
if (!Array.prototype.toSorted) {
  Array.prototype.toSorted = function(compareFn) {
    return [...this].sort(compareFn);
  };
}

// Polyfill for toReversed method (ES2023) for older browsers
if (!Array.prototype.toReversed) {
  Array.prototype.toReversed = function() {
    return [...this].reverse();
  };
}

// Polyfill for toSpliced method (ES2023) for older browsers
if (!Array.prototype.toSpliced) {
  Array.prototype.toSpliced = function(start, deleteCount, ...items) {
    const newArray = [...this];
    newArray.splice(start, deleteCount, ...items);
    return newArray;
  };
}

// Polyfill for with method (ES2023) for older browsers
if (!Array.prototype.with) {
  Array.prototype.with = function(index, value) {
    const newArray = [...this];
    newArray[index] = value;
    return newArray;
  };
}

// Setup global error handlers to catch all JavaScript errors
setupGlobalErrorHandlers();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistedStore}>
      <NetworkGate>
        <OfflineEventsBridge />
        <OfflineDraftAutoSync />
        <OfflineBanner />
        <SyncProgressBar />
        <App />
      </NetworkGate>
    </PersistGate>
  </Provider>
);
